/**
 * Theme definitions for Vendor Portal.
 *
 * The portal now ships a SINGLE premium brand identity (the 7-theme switcher was
 * retired). This module exists only as a compatibility layer: the ~20 screens
 * that still call `useTheme().theme.colors.*` keep working, and every theme id
 * resolves to the same brand palette. The palette is built FROM
 * `lib/designTokens.ts`, so brand colors are tuned in exactly one place.
 */

import type { Theme, ThemeColors, ThemeId } from './types';
import { brand, status, surface } from './designTokens';

// The single brand palette, mapped onto the load-bearing 28-key ThemeColors
// contract that existing screens consume.
const brandColors: ThemeColors = {
  // Primary
  primary: brand.primary,
  primaryHover: brand.primaryHover,
  primaryLight: brand.primaryLight,
  primaryDark: brand.primaryDark,
  // Secondary
  secondary: brand.secondary,
  secondaryHover: brand.secondaryHover,
  secondaryLight: brand.secondaryLight,
  // Accent
  accent: brand.accent,
  accentHover: brand.accentHover,
  accentLight: brand.accentLight,
  // Status
  success: status.success.base,
  successLight: status.success.soft,
  warning: status.warning.base,
  warningLight: status.warning.soft,
  error: status.error.base,
  errorLight: status.error.soft,
  info: status.info.base,
  infoLight: status.info.soft,
  // Sidebar — light surface, brand accent
  sidebar: '#F8FAFC',
  sidebarHover: '#F1F5F9',
  sidebarActive: brand.primary,
  sidebarText: '#1E293B',
  sidebarLogo: '#FFFFFF',
  sidebarFooter: '#FFFFFF',
  // Header
  headerBg: '#FFFFFF',
  headerText: '#1E293B',
  // Buttons
  buttonBg: brand.primary,
  buttonHover: brand.primaryHover,
  // Misc
  cardBorder: surface.border,
  progressBar: brand.primary,
  // Table
  tableHeader: '#F8FAFC',
  tableHeaderText: '#475569',
  tableRowHover: '#F1F5F9',
};

export const brandTheme: Theme = {
  id: 1,
  name: 'Brand',
  description: 'Vendor Portal brand identity',
  preview: brand.primary,
  colors: brandColors,
};

// Every id resolves to the brand theme. Kept as a 7-key record so any lingering
// `themes[id]` lookup (e.g. in the soon-to-be-removed ThemeSelector) stays safe.
export const themes: Record<ThemeId, Theme> = {
  1: brandTheme,
  2: brandTheme,
  3: brandTheme,
  4: brandTheme,
  5: brandTheme,
  6: brandTheme,
  7: brandTheme,
};

// The argument is ignored — there is only one identity now.
export const getTheme = (_themeId?: ThemeId): Theme => brandTheme;

export const getThemeList = (): Theme[] => [brandTheme];
